import React, { useMemo } from 'react';
import { Menu } from 'antd';
import { DashboardOutlined, TeamOutlined, HistoryOutlined, FileImageOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { ItemType, MenuItemType } from 'antd/es/menu/interface';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'app/atoms';
import { PermissionActions, PermissionSubjects } from 'configs/permissionConstants';
import { ROOT, CONTENT_ITEMS, USER_MANAGEMENT, AUDIT_LOGS } from 'configs/routeNames';

interface SidebarProps {
  collapsed: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const ability = useAbility(AbilityContext);

  const menuItems: ItemType<MenuItemType>[] = useMemo(() => {
    const allMenuItems: Array<ItemType<MenuItemType> & { requiredPermission?: { action: string; subject: string } }> = [
      {
        key: ROOT,
        icon: <DashboardOutlined />,
        label: 'Dashboard',
        requiredPermission: { action: PermissionActions.VIEW, subject: PermissionSubjects.DASHBOARD },
      },
      {
        key: `/${CONTENT_ITEMS}`,
        icon: <FileImageOutlined />,
        label: 'Onboarding Screens',
        requiredPermission: { action: PermissionActions.VIEW, subject: PermissionSubjects.CONTENT_ITEMS },
      },
      {
        key: `/${USER_MANAGEMENT}`,
        icon: <TeamOutlined />,
        label: 'User Management',
        requiredPermission: { action: PermissionActions.MANAGE, subject: PermissionSubjects.USER_MANAGEMENT },
      },
      {
        key: `/${AUDIT_LOGS}`,
        icon: <HistoryOutlined />,
        label: 'Activity Logs',
        requiredPermission: { action: PermissionActions.VIEW, subject: PermissionSubjects.ACTIVITY_LOGS },
      },
    ];

    return allMenuItems
      .filter((item) => !item.requiredPermission || ability.can(item.requiredPermission.action, item.requiredPermission.subject))
      .map(({ requiredPermission: _requiredPermission, ...item }) => item);
  }, [ability]);

  return (
    <aside
      className={classNames('h-full shadow-sm bg-white transition-all duration-200', {
        'w-[70px]': collapsed,
        'w-[220px]': !collapsed,
      })}
    >
      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        onClick={({ key }) => {
          navigate(key);
          onClose?.();
        }}
        items={menuItems}
        inlineCollapsed={collapsed}
        className="h-full"
      />
    </aside>
  );
};

export default Sidebar;
